import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function DatabaseSetup({ onComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await apiFetch('/api/migrations/status');
      const data = await res.json();
      setStatus(data);
      setLoading(false);
    } catch (err) {
      console.error('Error checking status:', err);
      setLoading(false);
    }
  };

  const runMigrations = async () => {
    setRunning(true);
    setResults(null);
    
    try {
      const res = await apiFetch('/api/migrations/run-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await res.json();
      setResults(data);
      
      // Re-check status after migrations
      setTimeout(() => {
        checkStatus();
      }, 1000);
      
    } catch (err) {
      setResults({
        success: false,
        message: 'Failed to run migrations',
        error: err.message
      });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking database status...</p>
        </div>
      </div>
    );
  }

  if (status?.ready) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Database Ready!</h2>
          <p className="text-gray-600 mb-6">All tables are set up and ready to use.</p>
          <button
            onClick={onComplete}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Continue to DoorBase →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🔧</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Database Setup Required</h1>
          <p className="text-gray-600">DoorBase needs to set up the database tables.</p>
        </div>

        {/* Status */}
        <div className="mb-8">
          <h3 className="font-semibold text-gray-800 mb-3">Current Status:</h3>
          <div className="space-y-2">
            <StatusItem label="Customers table" ready={status?.tables?.customers} />
            <StatusItem label="Sites table" ready={status?.tables?.sites} />
            <StatusItem label="Invoices table" ready={status?.tables?.invoices} />
            <StatusItem label="Invoice items table" ready={status?.tables?.invoice_items} />
            <StatusItem label="Payment terms table" ready={status?.tables?.payment_terms} />
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className={`mb-6 p-4 rounded-lg ${
            results.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <p className={`font-medium ${
              results.success ? 'text-green-800' : 'text-red-800'
            }`}>
              {results.message}
            </p>
            {results.results && (
              <div className="mt-3 space-y-1 text-sm">
                {results.results.map((r, i) => (
                  <div key={i} className="flex items-center">
                    <span className="mr-2">{r.status === 'success' ? '✅' : '❌'}</span>
                    <span className="text-gray-700">{r.file}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Button */}
        <div className="text-center">
          <button
            onClick={runMigrations}
            disabled={running}
            className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {running ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Running Migrations...
              </span>
            ) : (
              '🚀 Setup Database Now'
            )}
          </button>
          
          <p className="text-sm text-gray-500 mt-4">
            This will create all necessary database tables and fields.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusItem({ label, ready }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
      <span className="text-gray-700">{label}</span>
      <span className={`font-medium ${ready ? 'text-green-600' : 'text-orange-600'}`}>
        {ready ? '✅ Ready' : '⚠️ Missing'}
      </span>
    </div>
  );
}

export default DatabaseSetup;
